import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "sonner";
import { 
  Plus, Trash2, MoveUp, MoveDown, Image as ImageIcon, Type, Minus, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify, RotateCw, Maximize2,
  Upload, Wand2, Eye, Palette, Square, Loader2, Link as LinkIcon,
  Newspaper, FileText, Mail, Copy, ClipboardPaste, Pin, PinOff, Move, X, Minimize2
} from "lucide-react";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");
const DEFAULT_FOOTER_LOGO_URL = `${API_URL}/api/articulos/imagen/qasesores-default-footer.jpg`;

// Section types - expanded with button
const SECTION_TYPES = {
  image: { label: "Imagen", icon: ImageIcon },
  text: { label: "Texto", icon: Type },
  button: { label: "Botón", icon: LinkIcon },
  imageText: { label: "Imagen + Texto", icon: Square },
  divider: { label: "Divisor", icon: Minus },
  spacer: { label: "Espacio", icon: Square }
};

// Default content block for text sections
const createContentBlock = (blockType = "text") => {
  if (blockType === "button") {
    return {
      id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
      type: "button",
      buttonText: "Ver más",
      buttonUrl: "",
      buttonBgColor: "#3b82f6",
      buttonTextColor: "#ffffff",
      buttonAlign: "center",
      buttonBorderRadius: 8,
    };
  }
  return {
    id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
    type: "text",
    content: "",
  };
};

// Default section configurations
const createSection = (type) => ({
  id: Date.now().toString(),
  type,
  // Image settings
  imageUrl: "",
  imageWidth: 100,
  imageHeight: "auto",
  imageAlign: "center",
  imageRotate: 0,
  imageMargin: 0,
  // Text settings
  text: "",
  textColor: "#333333",
  textSize: 16,
  textAlign: "left",
  textLineHeight: 1.6,
  textIndent: 0,
  textMaxWidth: 0,
  // Content blocks for text sections (text + inline buttons)
  contentBlocks: [createContentBlock("text")],
  // Button settings (for standalone button sections)
  buttonText: "Ver más",
  buttonUrl: "",
  buttonBgColor: "#3b82f6",
  buttonTextColor: "#ffffff",
  buttonAlign: "center",
  buttonBorderRadius: 8,
  // Layout settings — sin espacio por defecto. Si el usuario quiere separar
  // dos secciones, debe agregar una sección "Espacio" o un "Divisor".
  backgroundColor: "transparent",
  padding: 0,
  // For imageText
  imagePosition: "left", // left, right, top, bottom
});

// Button Section Editor
function ButtonSectionEditor({ section, onChange }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Texto del Botón</Label>
        <Input
          value={section.buttonText}
          onChange={(e) => onChange({ ...section, buttonText: e.target.value })}
          placeholder="Ver más"
        />
      </div>
      <div>
        <Label className="text-xs">URL de Destino *</Label>
        <Input
          value={section.buttonUrl}
          onChange={(e) => onChange({ ...section, buttonUrl: e.target.value })}
          placeholder="https://ejemplo.com/pagina"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Color Fondo</Label>
          <div className="flex gap-2">
            <input
              type="color"
              value={section.buttonBgColor}
              onChange={(e) => onChange({ ...section, buttonBgColor: e.target.value })}
              className="w-8 h-8 rounded cursor-pointer"
            />
            <Input
              value={section.buttonBgColor}
              onChange={(e) => onChange({ ...section, buttonBgColor: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Color Texto</Label>
          <div className="flex gap-2">
            <input
              type="color"
              value={section.buttonTextColor}
              onChange={(e) => onChange({ ...section, buttonTextColor: e.target.value })}
              className="w-8 h-8 rounded cursor-pointer"
            />
            <Input
              value={section.buttonTextColor}
              onChange={(e) => onChange({ ...section, buttonTextColor: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs">Alineación</Label>
        <div className="flex gap-1">
          {["left", "center", "right"].map((align) => (
            <Button
              key={align}
              variant={section.buttonAlign === align ? "default" : "outline"}
              size="sm"
              onClick={() => onChange({ ...section, buttonAlign: align })}
            >
              {align === "left" && <AlignLeft className="w-4 h-4" />}
              {align === "center" && <AlignCenter className="w-4 h-4" />}
              {align === "right" && <AlignRight className="w-4 h-4" />}
            </Button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs">Bordes Redondeados: {section.buttonBorderRadius}px</Label>
        <Slider
          value={[section.buttonBorderRadius]}
          onValueChange={([v]) => onChange({ ...section, buttonBorderRadius: v })}
          min={0}
          max={30}
          step={1}
        />
      </div>
    </div>
  );
}

// Image Section Editor
function ImageSectionEditor({ section, onChange, onRemoveBackground, isRemovingBg, token }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API_URL}/api/articulos/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        onChange({ ...section, imageUrl: data.url });
        toast.success("Imagen cargada");
      } else {
        toast.error("Error al cargar imagen");
      }
    } catch (error) {
      toast.error("Error al cargar imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Image URL / Upload */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Imagen</Label>
        <div className="flex gap-2">
          <Input
            value={section.imageUrl}
            onChange={(e) => onChange({ ...section, imageUrl: e.target.value })}
            placeholder="URL de la imagen"
            className="flex-1 text-sm"
          />
          <Button variant="outline" size="sm" asChild disabled={uploading}>
            <label className="cursor-pointer">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            </label>
          </Button>
        </div>
      </div>

      {section.imageUrl && (
        <>
          {/* Image Preview */}
          <div 
            className="border rounded-lg p-2 bg-gray-50"
            style={{ textAlign: section.imageAlign }}
          >
            <img
              src={section.imageUrl}
              alt="Preview"
              className="inline-block rounded"
              style={{
                maxWidth: `${section.imageWidth}%`,
                transform: `rotate(${section.imageRotate}deg)`,
                height: section.imageHeight !== "auto" ? `${section.imageHeight}px` : "auto"
              }}
            />
          </div>

          {/* Remove Background Button */}
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => onRemoveBackground(section)}
            disabled={isRemovingBg}
            data-testid="remove-background-btn"
          >
            {isRemovingBg ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4" />
            )}
            {isRemovingBg ? "Removiendo fondo..." : "Quitar fondo con IA"}
          </Button>

          {/* Width */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label className="text-xs">Ancho</Label>
              <span className="text-xs text-gray-500">{section.imageWidth}%</span>
            </div>
            <Slider
              value={[section.imageWidth]}
              onValueChange={([val]) => onChange({ ...section, imageWidth: val })}
              min={20}
              max={100}
              step={5}
            />
          </div>

          {/* Height */}
          <div className="space-y-2">
            <Label className="text-xs">Altura</Label>
            <Select
              value={section.imageHeight}
              onValueChange={(val) => onChange({ ...section, imageHeight: val })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automática</SelectItem>
                <SelectItem value="50">50px</SelectItem>
                <SelectItem value="100">100px</SelectItem>
                <SelectItem value="150">150px</SelectItem>
                <SelectItem value="200">200px</SelectItem>
                <SelectItem value="300">300px</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Alignment */}
          <div className="space-y-2">
            <Label className="text-xs">Alineación</Label>
            <div className="flex gap-1">
              {["left", "center", "right"].map((align) => (
                <Button
                  key={align}
                  variant={section.imageAlign === align ? "default" : "outline"}
                  size="sm"
                  className="flex-1 h-8"
                  onClick={() => onChange({ ...section, imageAlign: align })}
                >
                  {align === "left" && <AlignLeft className="w-4 h-4" />}
                  {align === "center" && <AlignCenter className="w-4 h-4" />}
                  {align === "right" && <AlignRight className="w-4 h-4" />}
                </Button>
              ))}
            </div>
          </div>

          {/* Rotation */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label className="text-xs">Rotación</Label>
              <span className="text-xs text-gray-500">{section.imageRotate}°</span>
            </div>
            <Slider
              value={[section.imageRotate]}
              onValueChange={([val]) => onChange({ ...section, imageRotate: val })}
              min={-180}
              max={180}
              step={5}
            />
          </div>

          {/* Margin */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label className="text-xs">Margen</Label>
              <span className="text-xs text-gray-500">{section.imageMargin}px</span>
            </div>
            <Slider
              value={[section.imageMargin]}
              onValueChange={([val]) => onChange({ ...section, imageMargin: val })}
              min={0}
              max={50}
              step={5}
            />
          </div>
        </>
      )}
    </div>
  );
}

// Text Section Editor
// WYSIWYG editable block — uses an uncontrolled contentEditable div
// to avoid the "cursor jumping back" bug that happens when React re-renders
// dangerouslySetInnerHTML on every keystroke. The DOM manages its own value;
// React only reads innerHTML on input/blur via onChange. The toolbar-passed
// `editorRef` allows format buttons (bold/italic/etc) to focus and execCommand.

// ============ PREVIEW IFRAME ============
// Renders the email HTML in a sandboxed iframe with the SAME CSS reset that
// the backend uses to render the PNG (see routes/articulos.py render_html_to_png).
// This guarantees the preview pane shows EXACTLY what recipients will see in
// their inbox or in the rendered image — without inheriting any Tailwind /
// app-level CSS from the parent document.
//
// The iframe auto-resizes its height to the content so the preview pane
// scrolls naturally inside the parent ScrollArea.
const EmailPreviewFrame = ({ html, width }) => {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(400);
  const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>
  html,body{margin:0!important;padding:0!important;background:#ffffff;}
  *{box-sizing:border-box;}
  body{font-family:Arial,Helvetica,sans-serif;-webkit-font-smoothing:antialiased;}
  p{margin:0!important;padding:0!important;}
  div{margin:0!important;}
  table{border-collapse:collapse;border-spacing:0;}
  td,tr{padding:0;}
  a[style*="display: inline-block"]{padding:14px 40px!important;}
</style></head><body>${html || ""}</body></html>`;
  // Resize the iframe to fit content whenever the HTML changes.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const resize = () => {
      try {
        const doc = frame.contentDocument || frame.contentWindow?.document;
        if (!doc) return;
        const h = Math.max(
          doc.documentElement.scrollHeight,
          doc.body?.scrollHeight || 0
        );
        setHeight(h + 4); // small buffer
      } catch { /* same-origin should always work for srcDoc */ }
    };
    frame.addEventListener("load", resize);
    // Some images may load after the initial load event fires.
    const t = setTimeout(resize, 200);
    const t2 = setTimeout(resize, 800);
    return () => {
      frame.removeEventListener("load", resize);
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [docHtml]);
  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <iframe
        ref={frameRef}
        title="Email Preview"
        srcDoc={docHtml}
        style={{ width: "100%", height: `${height}px`, border: 0, display: "block" }}
        data-testid="email-preview-iframe"
      />
    </div>
  );
};

const WysiwygBlock = forwardRef(function WysiwygBlock(
  { initialHtml, onChange, style, dataTestId, className },
  ref
) {
  const internalRef = useRef(null);
  // Expose the inner DOM node to the parent (used by toolbar)
  useImperativeHandle(ref, () => internalRef.current, []);
  // Set the initial HTML once on mount. After that, React must NOT touch
  // innerHTML — otherwise typing resets cursor to the start.
  useEffect(() => {
    if (internalRef.current && internalRef.current.innerHTML !== (initialHtml || "")) {
      // Only sync when the external value differs significantly (e.g. on first
      // mount, or after a programmatic change like format painter). This avoids
      // overwriting the DOM during normal typing.
      const isFocused = document.activeElement === internalRef.current;
      if (!isFocused) {
        internalRef.current.innerHTML = initialHtml || "";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHtml]);
  return (
    <div
      ref={internalRef}
      contentEditable
      suppressContentEditableWarning
      onInput={(e) => onChange(e.currentTarget.innerHTML)}
      onBlur={(e) => onChange(e.currentTarget.innerHTML)}
      onPaste={(e) => {
        // Paste as plain text to avoid bringing in external styles/fonts that
        // break the email layout.
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      className={className}
      style={style}
      data-testid={dataTestId}
    />
  );
});

function TextSectionEditor({ section, onChange, copiedTextFormat, onCopyFormat, onPasteFormat }) {
  // Backward compat: if no contentBlocks, build from existing text
  const blocks = section.contentBlocks && section.contentBlocks.length > 0
    ? section.contentBlocks
    : [{ id: "legacy", type: "text", content: section.text || "" }];

  // Predefined color swatches (covers brand + common usage). Custom color still
  // available via the native picker as a fallback (last cell with rainbow icon).
  const COLOR_SWATCHES = [
    "#000000", "#374151", "#6B7280", "#9CA3AF",
    "#FFFFFF", "#FEF3C7", "#FECACA", "#DBEAFE",
    "#1E3A8A", "#193966", "#1D4ED8", "#2563EB",
    "#DC2626", "#EA580C", "#D97706", "#CA8A04",
    "#16A34A", "#059669", "#0891B2", "#7C3AED",
  ];
  const HIGHLIGHT_SWATCHES = [
    "transparent", "#FEF08A", "#FECACA", "#BBF7D0",
    "#BFDBFE", "#E9D5FF", "#FED7AA", "#C5C4C4",
  ];

  const updateBlocks = (newBlocks) => {
    // Also keep text field synced for backward compat (concat all text blocks)
    const plainText = newBlocks.filter(b => b.type === "text").map(b => b.content).join("\n");
    onChange({ ...section, contentBlocks: newBlocks, text: plainText });
  };

  const updateBlock = (blockId, updates) => {
    updateBlocks(blocks.map(b => b.id === blockId ? { ...b, ...updates } : b));
  };

  const removeBlock = (blockId) => {
    const newBlocks = blocks.filter(b => b.id !== blockId);
    if (newBlocks.length === 0) newBlocks.push(createContentBlock("text"));
    updateBlocks(newBlocks);
  };

  const insertBlockAfter = (afterId, blockType) => {
    const idx = blocks.findIndex(b => b.id === afterId);
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, createContentBlock(blockType));
    updateBlocks(newBlocks);
  };

  const insertBlockAtStart = (blockType) => {
    updateBlocks([createContentBlock(blockType), ...blocks]);
  };

  const [expandedBtn, setExpandedBtn] = useState(null);

  return (
    <div className="space-y-1">
      {/* Global text styles */}
      <div className="space-y-2 mb-3">
        <div>
          <Label className="text-xs">Color del texto</Label>
          <div className="flex flex-wrap gap-1 items-center mt-1">
            {COLOR_SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChange({ ...section, textColor: c })}
                className={`w-6 h-6 rounded border-2 transition-transform hover:scale-110 ${section.textColor?.toLowerCase() === c.toLowerCase() ? "border-blue-500 ring-2 ring-blue-300" : "border-slate-300"}`}
                style={{ background: c }}
                title={c}
                data-testid={`text-color-swatch-${c.replace("#","")}`}
              />
            ))}
            {/* Custom color picker for any other shade */}
            <label className="w-6 h-6 rounded border-2 border-slate-300 cursor-pointer overflow-hidden relative" title="Color personalizado">
              <input type="color" value={section.textColor} onChange={(e) => onChange({ ...section, textColor: e.target.value })} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
              <span className="absolute inset-0 flex items-center justify-center text-xs">🎨</span>
            </label>
            <Input value={section.textColor} onChange={(e) => onChange({ ...section, textColor: e.target.value })} className="w-20 text-xs h-7" />
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <div className="w-20">
            <Label className="text-xs">Tamaño</Label>
            <Input type="number" min={10} max={40} value={section.textSize} onChange={(e) => onChange({ ...section, textSize: parseInt(e.target.value) || 16 })} className="text-xs h-7" />
          </div>
          <div>
            <Label className="text-xs">Alinear</Label>
            <div className="flex gap-0.5">
              {["left", "center", "right", "justify"].map((a) => (
                <Button key={a} variant={section.textAlign === a ? "default" : "outline"} size="sm" className="h-7 w-7 p-0" onClick={() => onChange({ ...section, textAlign: a })} title={a === "justify" ? "Justificar" : a.charAt(0).toUpperCase() + a.slice(1)}>
                  {a === "left" && <AlignLeft className="w-3 h-3" />}
                  {a === "center" && <AlignCenter className="w-3 h-3" />}
                  {a === "right" && <AlignRight className="w-3 h-3" />}
                  {a === "justify" && <AlignJustify className="w-3 h-3" />}
                </Button>
              ))}
            </div>
          </div>
          {/* Copy/paste format (Format Painter) */}
          <div>
            <Label className="text-xs">Formato</Label>
            <div className="flex gap-0.5">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                title="Copiar formato (color, tamaño, alineación, interlineado, sangría, ancho)"
                onClick={() => onCopyFormat && onCopyFormat({
                  textColor: section.textColor,
                  textSize: section.textSize,
                  textAlign: section.textAlign,
                  textLineHeight: section.textLineHeight,
                  textIndent: section.textIndent,
                  textMaxWidth: section.textMaxWidth,
                })}
                data-testid="copy-format-btn"
              >
                <Copy className="w-3 h-3" />
              </Button>
              <Button
                variant={copiedTextFormat ? "default" : "outline"}
                size="sm"
                className="h-7 w-7 p-0"
                disabled={!copiedTextFormat}
                title={copiedTextFormat ? "Pegar formato copiado" : "Primero copia un formato desde otra sección"}
                onClick={() => onPasteFormat && copiedTextFormat && onChange({ ...section, ...copiedTextFormat })}
                data-testid="paste-format-btn"
              >
                <ClipboardPaste className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
        {/* Advanced typography: line-height, text-indent, max-width */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px]">Interlineado</Label>
            <Input
              type="number"
              step="0.1"
              min={1}
              max={3}
              value={section.textLineHeight ?? 1.6}
              onChange={(e) => onChange({ ...section, textLineHeight: parseFloat(e.target.value) || 1.6 })}
              className="text-xs h-7"
              title="Espaciado entre líneas (1.0 compacto — 3.0 amplio)"
            />
          </div>
          <div>
            <Label className="text-[10px]">Sangría 1er párrafo (px)</Label>
            <Input
              type="number"
              min={0}
              max={80}
              value={section.textIndent ?? 0}
              onChange={(e) => onChange({ ...section, textIndent: parseInt(e.target.value) || 0 })}
              className="text-xs h-7"
              title="Indentación solo del primer párrafo (0 = sin sangría)"
            />
          </div>
          <div>
            <Label className="text-[10px]">Ancho máx. (px)</Label>
            <Input
              type="number"
              min={0}
              max={800}
              value={section.textMaxWidth ?? 0}
              onChange={(e) => onChange({ ...section, textMaxWidth: parseInt(e.target.value) || 0 })}
              className="text-xs h-7"
              title="Ancho máximo del texto (0 = usa ancho completo)"
            />
          </div>
        </div>
      </div>

      {/* Insert at top */}
      <div className="flex items-center justify-center py-0.5">
        <div className="flex-1 h-px bg-slate-200" />
        <Popover>
          <PopoverTrigger asChild>
            <button className="mx-1 w-5 h-5 rounded-full bg-emerald-100 hover:bg-emerald-500 hover:text-white text-emerald-600 flex items-center justify-center text-[10px] font-bold transition-colors border border-emerald-300 hover:border-emerald-500" title="Insertar bloque al inicio">
              <Plus className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-36 p-1.5" side="right">
            <button onClick={() => insertBlockAtStart("text")} className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-blue-50 w-full text-left">
              <Type className="w-3 h-3" /> Texto
            </button>
            <button onClick={() => insertBlockAtStart("button")} className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-blue-50 w-full text-left">
              <LinkIcon className="w-3 h-3" /> Botón
            </button>
          </PopoverContent>
        </Popover>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Content blocks */}
      {blocks.map((block) => (
        <div key={block.id}>
          {block.type === "text" ? (
            <div className="relative group/block">
              {/* WYSIWYG inline format toolbar — applies real formatting to current
                  selection inside the contentEditable below using execCommand. The
                  user sees rich text directly (bold, italic, etc.), not raw HTML tags. */}
              <div className="flex flex-wrap items-center gap-1 mb-1 p-1 bg-slate-50 border rounded">
                {[
                  { cmd: "bold", icon: "B", title: "Negrita", weight: 700 },
                  { cmd: "italic", icon: "I", title: "Cursiva", italic: true },
                  { cmd: "underline", icon: "U", title: "Subrayado", underline: true },
                  { cmd: "strikeThrough", icon: "S", title: "Tachado", strike: true },
                ].map(({ cmd, icon, title, weight, italic, underline, strike }) => (
                  <button
                    key={cmd}
                    type="button"
                    title={title}
                    data-testid={`fmt-${cmd}-${block.id}`}
                    className="w-7 h-7 rounded border bg-white hover:bg-blue-50 hover:border-blue-400 flex items-center justify-center text-xs"
                    onMouseDown={(e) => {
                      // Prevent contenteditable from losing focus/selection
                      e.preventDefault();
                      const editor = e.currentTarget.closest(".relative")?.querySelector('[contenteditable="true"]');
                      if (!editor) return;
                      editor.focus();
                      try { document.execCommand(cmd, false, null); } catch (_) {}
                      // Sync state with new HTML
                      updateBlock(block.id, { content: editor.innerHTML });
                    }}
                    style={{ fontWeight: weight, fontStyle: italic ? "italic" : undefined, textDecoration: underline ? "underline" : strike ? "line-through" : undefined }}
                  >
                    {icon}
                  </button>
                ))}
                {/* Clear formatting (Tx) — sanitizes the WHOLE block content.
                    Strips <font>, <span style>, inline color/font styles, and
                    unwraps invalid <p> wrapping <div> structures that Word /
                    Outlook paste in. Keeps text + basic semantic tags
                    (b/i/u/strong/em/br/p/div) so structure is preserved. */}
                <button
                  type="button"
                  title="Limpiar formato (quita estilos, colores, fuentes pegados desde Word/Outlook)"
                  data-testid={`fmt-clear-${block.id}`}
                  className="h-7 px-2 rounded border bg-white hover:bg-amber-50 hover:border-amber-400 flex items-center gap-0.5 text-[11px]"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const editor = e.currentTarget.closest(".relative")?.querySelector('[contenteditable="true"]');
                    if (!editor) return;
                    editor.focus();
                    // 1) execCommand removeFormat on the current selection (or full content if no selection).
                    const sel = window.getSelection();
                    const hadSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;
                    if (!hadSelection) {
                      // Select all so removeFormat affects everything
                      const range = document.createRange();
                      range.selectNodeContents(editor);
                      sel.removeAllRanges();
                      sel.addRange(range);
                    }
                    try {
                      document.execCommand("removeFormat", false, null);
                      document.execCommand("unlink", false, null);
                    } catch (_) {}
                    // 2) Aggressive cleanup on the resulting HTML:
                    //    - Remove <font> tags (unwrap, keep content)
                    //    - Remove style="" attributes that survived
                    //    - Unwrap <span> (keep content)
                    //    - Unwrap <p> that wraps block-level <div>s (HTML invalid)
                    let html = editor.innerHTML;
                    // Unwrap <font>
                    html = html.replace(/<\/?font\b[^>]*>/gi, "");
                    // Strip class= and style= attributes globally
                    html = html.replace(/\s+(class|style)\s*=\s*"[^"]*"/gi, "");
                    html = html.replace(/\s+(class|style)\s*=\s*'[^']*'/gi, "");
                    // Unwrap <span> (no semantic meaning after stripping styles)
                    html = html.replace(/<\/?span\b[^>]*>/gi, "");
                    // Unwrap <p> that contains <div>s
                    let prev;
                    do {
                      prev = html;
                      html = html.replace(
                        /<p\b[^>]*>([\s\S]*?)<\/p>/gi,
                        (m, inner) => /<(div|table|h[1-6]|ul|ol|blockquote|pre)\b/i.test(inner) ? inner : m
                      );
                    } while (prev !== html);
                    // Collapse multiple <br> and strip trailing empty blocks.
                    // Iterate the trim regex because whitespace between divs
                    // prevents a single pass from matching multiple consecutive
                    // empty blocks.
                    html = html.replace(/(?:<br\s*\/?>\s*){3,}/gi, "<br><br>");
                    let prevTrim;
                    do {
                      prevTrim = html;
                      html = html.replace(
                        /(?:\s*<(?:p|div)(?:\s[^>]*)?>\s*(?:&nbsp;|\s|<br\s*\/?>)*\s*<\/(?:p|div)>|\s*<br\s*\/?>)\s*$/i,
                        ""
                      );
                    } while (prevTrim !== html);
                    editor.innerHTML = html;
                    // Collapse selection at the end and sync state
                    if (sel) sel.collapseToEnd();
                    updateBlock(block.id, { content: editor.innerHTML });
                  }}
                >
                  <span className="font-mono text-amber-700">Tx</span>
                </button>
                {/* Highlight (background) — applied to selection using execCommand */}
                <div className="flex items-center gap-0.5 ml-1 pl-1 border-l">
                  <span className="text-[10px] text-slate-500 mr-0.5">Resaltado:</span>
                  {HIGHLIGHT_SWATCHES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      title={c === "transparent" ? "Sin resaltado" : `Resaltar ${c}`}
                      data-testid={`fmt-mark-${block.id}-${c.replace("#","").replace("transparent","none")}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const editor = e.currentTarget.closest(".relative")?.querySelector('[contenteditable="true"]');
                        if (!editor) return;
                        editor.focus();
                        try {
                          // hiliteColor is the standard for background highlight; fallback to backColor
                          const ok = document.execCommand("hiliteColor", false, c === "transparent" ? "transparent" : c);
                          if (!ok) document.execCommand("backColor", false, c === "transparent" ? "transparent" : c);
                        } catch (_) {}
                        updateBlock(block.id, { content: editor.innerHTML });
                      }}
                      className={`w-5 h-5 rounded border ${c === "transparent" ? "bg-white relative" : ""}`}
                      style={c !== "transparent" ? { background: c } : undefined}
                    >
                      {c === "transparent" && <span className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400">∅</span>}
                    </button>
                  ))}
                </div>
                {/* Insertar variable de contacto — sustituida en backend al enviar */}
                <div className="ml-1 pl-1 border-l">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        title="Insertar variable de contacto (Nombre, Empresa, etc.)"
                        data-testid={`fmt-insertvar-${block.id}`}
                        className="h-7 px-2 rounded border bg-white hover:bg-emerald-50 hover:border-emerald-400 flex items-center gap-1 text-[11px] text-emerald-700"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <span className="font-mono text-emerald-600">{"{{ }}"}</span>
                        <span>Variable</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-2" side="bottom" align="start">
                      <p className="text-[11px] font-medium text-slate-600 mb-1">Insertar variable de contacto</p>
                      <p className="text-[10px] text-slate-500 mb-2">Se reemplazará por el dato del contacto al enviar.</p>
                      {[
                        { key: "nombre", label: "Nombre" },
                        { key: "email", label: "Email" },
                        { key: "empresa", label: "Empresa" },
                        { key: "telefono", label: "Teléfono" },
                        { key: "clase_cliente", label: "Clase Cliente" },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          data-testid={`insert-var-${key}-${block.id}`}
                          className="flex w-full items-center justify-between gap-2 px-2 py-1 rounded text-xs hover:bg-emerald-50 text-slate-700"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const editor = e.currentTarget.closest('[role="dialog"]')?.parentElement?.parentElement
                              ? document.querySelector(`[data-testid="text-editor-${block.id}"]`)
                              : null;
                            // Fallback: lookup by data-testid directly
                            const target = editor || document.querySelector(`[data-testid="text-editor-${block.id}"]`);
                            if (!target) return;
                            target.focus();
                            try {
                              document.execCommand("insertText", false, `{{${key}}}`);
                            } catch (_) {}
                            updateBlock(block.id, { content: target.innerHTML });
                          }}
                        >
                          <span>{label}</span>
                          <span className="font-mono text-emerald-600 text-[10px]">{`{{${key}}}`}</span>
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <WysiwygBlock
                initialHtml={block.content}
                onChange={(html) => updateBlock(block.id, { content: html })}
                className="text-sm pr-7 min-h-[80px] p-2 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 wysiwyg-editor"
                style={{ color: section.textColor, fontSize: section.textSize, textAlign: section.textAlign, lineHeight: section.textLineHeight }}
                dataTestId={`text-editor-${block.id}`}
              />
              {blocks.length > 1 && (
                <button onClick={() => removeBlock(block.id)} className="absolute top-1 right-1 w-5 h-5 rounded bg-red-100 hover:bg-red-500 hover:text-white text-red-500 flex items-center justify-center opacity-0 group-hover/block:opacity-100 transition-opacity" title="Eliminar bloque">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : (
            /* Inline button block */
            <div className="relative border border-dashed border-blue-300 rounded-md p-2 bg-blue-50/50 group/block">
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center justify-center">
                  <span
                    className="inline-block px-4 py-1.5 rounded text-sm font-medium cursor-pointer"
                    style={{ backgroundColor: block.buttonBgColor, color: block.buttonTextColor, borderRadius: block.buttonBorderRadius + "px" }}
                    onClick={() => setExpandedBtn(expandedBtn === block.id ? null : block.id)}
                  >
                    {block.buttonText || "Botón"}
                  </span>
                </div>
                <button onClick={() => removeBlock(block.id)} className="w-5 h-5 rounded bg-red-100 hover:bg-red-500 hover:text-white text-red-500 flex items-center justify-center shrink-0" title="Eliminar botón">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {/* Expanded button settings */}
              {expandedBtn === block.id && (
                <div className="mt-2 pt-2 border-t border-blue-200 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">Texto</Label>
                      <Input value={block.buttonText} onChange={(e) => updateBlock(block.id, { buttonText: e.target.value })} className="h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px]">URL destino</Label>
                      <Input value={block.buttonUrl} onChange={(e) => updateBlock(block.id, { buttonUrl: e.target.value })} placeholder="https://..." className="h-7 text-xs" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[10px]">Fondo</Label>
                      <div className="flex gap-1"><input type="color" value={block.buttonBgColor} onChange={(e) => updateBlock(block.id, { buttonBgColor: e.target.value })} className="w-6 h-6 rounded cursor-pointer" /><Input value={block.buttonBgColor} onChange={(e) => updateBlock(block.id, { buttonBgColor: e.target.value })} className="h-6 text-[10px] flex-1" /></div>
                    </div>
                    <div>
                      <Label className="text-[10px]">Texto</Label>
                      <div className="flex gap-1"><input type="color" value={block.buttonTextColor} onChange={(e) => updateBlock(block.id, { buttonTextColor: e.target.value })} className="w-6 h-6 rounded cursor-pointer" /><Input value={block.buttonTextColor} onChange={(e) => updateBlock(block.id, { buttonTextColor: e.target.value })} className="h-6 text-[10px] flex-1" /></div>
                    </div>
                    <div>
                      <Label className="text-[10px]">Alinear</Label>
                      <div className="flex gap-0.5">
                        {["left", "center", "right"].map((a) => (
                          <Button key={a} variant={block.buttonAlign === a ? "default" : "outline"} size="sm" className="h-6 w-6 p-0" onClick={() => updateBlock(block.id, { buttonAlign: a })}>
                            {a === "left" && <AlignLeft className="w-2.5 h-2.5" />}
                            {a === "center" && <AlignCenter className="w-2.5 h-2.5" />}
                            {a === "right" && <AlignRight className="w-2.5 h-2.5" />}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px]">Bordes: {block.buttonBorderRadius}px</Label>
                    <Slider value={[block.buttonBorderRadius]} onValueChange={([v]) => updateBlock(block.id, { buttonBorderRadius: v })} min={0} max={30} step={1} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Insert between blocks */}
          <div className="flex items-center justify-center py-0.5">
            <div className="flex-1 h-px bg-slate-200" />
            <Popover>
              <PopoverTrigger asChild>
                <button className="mx-1 w-5 h-5 rounded-full bg-emerald-100 hover:bg-emerald-500 hover:text-white text-emerald-600 flex items-center justify-center text-[10px] font-bold transition-colors border border-emerald-300 hover:border-emerald-500" title="Insertar bloque aquí">
                  <Plus className="w-3 h-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-36 p-1.5" side="right">
                <button onClick={() => insertBlockAfter(block.id, "text")} className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-blue-50 w-full text-left">
                  <Type className="w-3 h-3" /> Texto
                </button>
                <button onClick={() => insertBlockAfter(block.id, "button")} className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-blue-50 w-full text-left">
                  <LinkIcon className="w-3 h-3" /> Botón
                </button>
              </PopoverContent>
            </Popover>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Section wrapper with common controls
function SectionEditor({ section, index, total, onChange, onDelete, onMove, onRemoveBackground, isRemovingBg, token, copiedTextFormat, onCopyFormat, onPasteFormat }) {
  const TypeIcon = SECTION_TYPES[section.type]?.icon || Square;

  return (
    <Card className="mb-3">
      <CardContent className="p-3">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b">
          <div className="flex items-center gap-2">
            <TypeIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">{SECTION_TYPES[section.type]?.label}</span>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMove(index, -1)}
              disabled={index === 0}
              className="h-7 w-7 p-0"
            >
              <MoveUp className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMove(index, 1)}
              disabled={index === total - 1}
              className="h-7 w-7 p-0"
            >
              <MoveDown className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(index)}
              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Section Content */}
        {section.type === "image" && (
          <ImageSectionEditor
            section={section}
            onChange={onChange}
            onRemoveBackground={onRemoveBackground}
            isRemovingBg={isRemovingBg}
            token={token}
          />
        )}
        {section.type === "text" && (
          <TextSectionEditor section={section} onChange={onChange} copiedTextFormat={copiedTextFormat} onCopyFormat={onCopyFormat} onPasteFormat={onPasteFormat} />
        )}
        {section.type === "imageText" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Posición de imagen</Label>
              <Select
                value={section.imagePosition}
                onValueChange={(val) => onChange({ ...section, imagePosition: val })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Izquierda</SelectItem>
                  <SelectItem value="right">Derecha</SelectItem>
                  <SelectItem value="top">Arriba</SelectItem>
                  <SelectItem value="bottom">Abajo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ImageSectionEditor
              section={section}
              onChange={onChange}
              onRemoveBackground={onRemoveBackground}
              isRemovingBg={isRemovingBg}
              token={token}
            />
            <TextSectionEditor section={section} onChange={onChange} copiedTextFormat={copiedTextFormat} onCopyFormat={onCopyFormat} onPasteFormat={onPasteFormat} />
          </div>
        )}
        {section.type === "divider" && (
          <div className="space-y-2">
            <Label className="text-xs">Color del divisor</Label>
            <input
              type="color"
              value={section.backgroundColor || "#e5e7eb"}
              onChange={(e) => onChange({ ...section, backgroundColor: e.target.value })}
              className="w-full h-8 rounded cursor-pointer border"
            />
          </div>
        )}
        {section.type === "spacer" && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label className="text-xs">Altura del espacio</Label>
              <span className="text-xs text-gray-500">{section.padding}px</span>
            </div>
            <Slider
              value={[section.padding]}
              onValueChange={([val]) => onChange({ ...section, padding: val })}
              min={10}
              max={100}
              step={10}
            />
          </div>
        )}

        {section.type === "button" && (
          <ButtonSectionEditor section={section} onChange={onChange} />
        )}

        {/* Common: Background Color (except divider/spacer) */}
        {!["divider", "spacer"].includes(section.type) && (
          <div className="space-y-2 mt-4 pt-4 border-t">
            <Label className="text-xs">Color de fondo de sección</Label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={section.backgroundColor === "transparent" ? "#ffffff" : section.backgroundColor}
                onChange={(e) => onChange({ ...section, backgroundColor: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer border"
              />
              <Input
                value={section.backgroundColor}
                onChange={(e) => onChange({ ...section, backgroundColor: e.target.value })}
                placeholder="transparent"
                className="flex-1 text-xs"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => onChange({ ...section, backgroundColor: "transparent" })}
                className="text-xs"
              >
                Quitar
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Generate HTML from sections with header/footer support
function generateHtml(sections, globalStyles, headerEnabled, headerSettings, footerEnabled, footerSettings, includeRecentArticles, recentArticulos, photoMode = null) {
  // El ancho total del email es dinámico — todo (header, body, footer) se
  // reajusta a ese ancho. En modo "Enviar como foto" (photoMode.enabled),
  // sobrescribimos el ancho con el de la imagen objetivo para que el render
  // ocupe TODA la fotografía sin dejar borde blanco a los lados.
  const W = photoMode?.enabled && photoMode.widthPx
    ? Math.max(320, Math.min(2000, parseInt(photoMode.widthPx, 10)))
    : Math.max(320, Math.min(1200, parseInt(globalStyles.emailWidth, 10) || 600));
  // IMPORTANTE: en modo foto NO forzamos altura mínima al HTML. El campo
  // "alto en pulgadas" se usa sólo como referencia/viewport en el backend;
  // la altura final de la foto la dicta el contenido real, así no quedan
  // gaps blancos entre secciones cuando el contenido es más corto que el
  // alto configurado. El usuario agrega "Espacios" si quiere más alto.
  let html = `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: ${globalStyles.backgroundColor};">
    <tr>
      <td align="center" valign="top">
        <table cellpadding="0" cellspacing="0" border="0" width="${W}" style="max-width: ${W}px; width: 100%; background-color: ${globalStyles.backgroundColor};">`;

  const mL = globalStyles.marginLeft || 0;
  const mR = globalStyles.marginRight || 0;
  const mT = globalStyles.marginTop || 0;
  const mB = globalStyles.marginBottom || 0;

  // Header — SIEMPRE cubre todo el ancho del marco. Los márgenes laterales
  // globales NO se aplican aquí (banners/logos se ven completos de borde a
  // borde, como el usuario espera al personalizar el tamaño del marco).
  if (headerEnabled) {
    const headerPad = headerSettings.padding || 0;
    html += `
          <tr>
            <td style="background-color: ${headerSettings.backgroundColor}; padding: ${headerPad}px 0 ${headerPad}px 0; text-align: center;">
              ${headerSettings.logoUrl ? `<img src="${headerSettings.logoUrl}" alt="Header" style="width: 100%; max-width: ${W}px; display: block; margin: 0; padding: 0;" />` : ""}
              ${headerSettings.title ? `<h1 style="margin: 0; color: ${headerSettings.textColor}; font-size: 24px; font-family: Arial, sans-serif;">${headerSettings.title}</h1>` : ""}
            </td>
          </tr>`;
  }

  // Main content — sin estiramiento vertical. La altura final del email se
  // ajusta naturalmente al contenido. Si el usuario quiere más separación,
  // agrega secciones "Espacio" explícitas.
  html += `
          <tr>
            <td valign="top" style="padding: ${mT}px ${mR}px ${mB}px ${mL}px; font-family: Arial, sans-serif;">`;

  // Sections are rendered back-to-back. Each section's own ``padding`` controls
  // its own breathing room. No hardcoded gap between sections so designs stay
  // tight unless the user explicitly adds a Spacer section.
  sections.forEach((section, index) => {
    const bgStyle = section.backgroundColor !== "transparent" 
      ? `background-color: ${section.backgroundColor};` 
      : "";

    if (section.type === "image" && section.imageUrl) {
      const alignStyle = section.imageAlign === "center" ? "margin: 0 auto; display: block;" :
                         section.imageAlign === "right" ? "margin-left: auto; display: block;" : "display: block;";
      html += `
              <div style="padding: ${section.imageMargin}px; ${bgStyle} text-align: ${section.imageAlign};">
                <img src="${section.imageUrl}" alt="Image" style="max-width: ${section.imageWidth}%; width: ${section.imageWidth}%; height: ${section.imageHeight === "auto" ? "auto" : section.imageHeight + "px"}; transform: rotate(${section.imageRotate}deg); ${alignStyle}" />
              </div>`;
    }

    if (section.type === "text") {
      // Support content blocks (text + inline buttons)
      const blocks = section.contentBlocks && section.contentBlocks.length > 0
        ? section.contentBlocks
        : [{ type: "text", content: section.text || "" }];

      let hasContent = blocks.some(b => (b.type === "text" && b.content) || b.type === "button");
      if (!hasContent) return; // skip empty

      // Advanced typography (email-safe: inline styles, fallbacks on 0)
      const lineH = section.textLineHeight || 1.6;
      const tIndent = section.textIndent || 0;
      const maxW = section.textMaxWidth || 0;
      const paraWrapStyle = maxW > 0
        ? `max-width: ${maxW}px; margin-left: auto; margin-right: auto;`
        : "";

      html += `
              <div style="padding: ${section.padding}px; ${bgStyle}">
                <div style="${paraWrapStyle}">`;

      let isFirstTextBlock = true;
      blocks.forEach((block) => {
        if (block.type === "text" && block.content) {
          const indentStyle = (isFirstTextBlock && tIndent > 0) ? ` text-indent: ${tIndent}px;` : "";
          isFirstTextBlock = false;
          // Limpieza agresiva de trailing whitespace y bloques vacíos. El
          // contentEditable del navegador inyecta <p><br></p>, <div><br></div>
          // o <br> al final cuando el usuario presiona Enter. Estos bloques
          // vacíos generan un gap visual antes de la siguiente sección/botón.
          // Iteramos para eliminar múltiples bloques vacíos consecutivos.
          let cleaned = String(block.content);
          let prev;
          do {
            prev = cleaned;
            cleaned = cleaned
              .replace(/[\s\u00A0]+$/g, "")
              .replace(/(?:<(?:p|div)(?:\s[^>]*)?>\s*(?:&nbsp;|\s|<br\s*\/?>)*\s*<\/(?:p|div)>|<br\s*\/?>)+\s*$/gi, "")
              .replace(/[\s\u00A0]+$/g, "");
          } while (cleaned !== prev);
          if (!cleaned) return; // skip if block is empty after cleaning
          // Inyectamos style="margin:0" en cada <p>/<div> generado por el
          // contentEditable (browser pone <p>, <div> al pulsar Enter). Si no,
          // heredan el margen 1em por defecto y queda gap antes del botón.
          cleaned = cleaned
            .replace(/<p(\s+[^>]*)?>/gi, (m, attrs = "") => {
              const a = attrs || "";
              if (/style\s*=/.test(a)) {
                // append margin:0 to existing style
                return m.replace(/style\s*=\s*"([^"]*)"/i, (mm, s) => `style="${s.replace(/;?\s*$/, "")}; margin:0;"`)
                       .replace(/style\s*=\s*'([^']*)'/i, (mm, s) => `style='${s.replace(/;?\s*$/, "")}; margin:0;'`);
              }
              return `<p${a} style="margin:0;">`;
            })
            .replace(/<div(\s+[^>]*)?>/gi, (m, attrs = "") => {
              const a = attrs || "";
              if (/style\s*=/.test(a)) {
                return m.replace(/style\s*=\s*"([^"]*)"/i, (mm, s) => `style="${s.replace(/;?\s*$/, "")}; margin:0;"`)
                       .replace(/style\s*=\s*'([^']*)'/i, (mm, s) => `style='${s.replace(/;?\s*$/, "")}; margin:0;'`);
              }
              return `<div${a} style="margin:0;">`;
            });
          // IMPORTANTE: usamos <div> en lugar de <p> como wrapper. Si el
          // contenido tiene <p> internos (vienen del execCommand al pulsar
          // Enter), HTML spec no permite <p> dentro de <p> y el navegador
          // autocierra el wrapper, generando líneas vacías con margen
          // por defecto de 1em (~16px). Con <div> evitamos ese parsing.
          html += `
                <div style="margin: 0; color: ${section.textColor}; font-size: ${section.textSize}px; text-align: ${section.textAlign}; line-height: ${lineH};${indentStyle}">${cleaned.replace(/\n/g, "<br>")}</div>`;
        }
        if (block.type === "button" && block.buttonText) {
          const btnUrl = block.buttonUrl && block.buttonUrl.trim() !== "" ? block.buttonUrl.trim() : "#";
          const btnAlign = block.buttonAlign || "center";
          html += `
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td align="${btnAlign}" style="padding: 0;">
                      <a href="${btnUrl}" target="_blank" style="display: inline-block; background-color: ${block.buttonBgColor}; color: ${block.buttonTextColor}; padding: 14px 40px; text-decoration: none; font-weight: bold; font-size: 16px; font-family: Arial, Helvetica, sans-serif; border-radius: ${block.buttonBorderRadius}px; text-align: center;">
                        ${block.buttonText}
                      </a>
                    </td>
                  </tr>
                </table>`;
        }
      });

      html += `
                </div>
              </div>`;
    }

    if (section.type === "imageText") {
      const isHorizontal = ["left", "right"].includes(section.imagePosition);
      const imageFirst = ["left", "top"].includes(section.imagePosition);
      
      const imageHtml = section.imageUrl ? `
              <div style="flex: 1; padding: ${section.imageMargin}px; text-align: ${section.imageAlign};">
                <img src="${section.imageUrl}" alt="Image" style="max-width: ${section.imageWidth}%; height: ${section.imageHeight === "auto" ? "auto" : section.imageHeight + "px"}; transform: rotate(${section.imageRotate}deg);" />
              </div>` : "";
      
      const textHtml = `
              <div style="flex: 1; padding: ${section.padding}px;">
                <p style="margin: 0; color: ${section.textColor}; font-size: ${section.textSize}px; text-align: ${section.textAlign}; line-height: ${section.textLineHeight || 1.6};${section.textIndent > 0 ? ` text-indent: ${section.textIndent}px;` : ""}${section.textMaxWidth > 0 ? ` max-width: ${section.textMaxWidth}px; margin-left: auto; margin-right: auto;` : ""}">
                  ${String(section.text || "").replace(/[\s\u00A0]+$/g, "").replace(/\n/g, "<br>")}
                </p>
              </div>`;

      html += `
              <div style="display: ${isHorizontal ? "flex" : "block"}; ${bgStyle}">
                ${imageFirst ? imageHtml + textHtml : textHtml + imageHtml}
              </div>`;
    }

    if (section.type === "divider") {
      html += `<hr style="border: none; border-top: 2px solid ${section.backgroundColor || "#e5e7eb"}; margin: 0;" />`;
    }

    if (section.type === "spacer") {
      // Si el padding es 0 no emitimos nada para evitar dejar un <div>
      // residual que podría tomar altura mínima en algunos clientes/render.
      if ((section.padding || 0) > 0) {
        html += `<div style="height: ${section.padding}px;"></div>`;
      }
    }

    if (section.type === "button" && section.buttonText) {
      const alignStyle = section.buttonAlign === "center" ? "text-align: center;" :
                         section.buttonAlign === "right" ? "text-align: right;" : "text-align: left;";
      // IMPORTANT: Use the user's configured URL, not a default
      // If URL is empty, use # to prevent redirect issues
      const buttonUrl = section.buttonUrl && section.buttonUrl.trim() !== "" 
        ? section.buttonUrl.trim() 
        : "#";
      
      // Use VML for better Outlook compatibility and larger padding
      html += `
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="${section.buttonAlign}" style="padding: 0;">
                    <!--[if mso]>
                    <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${buttonUrl}" style="height:50px;v-text-anchor:middle;width:250px;" arcsize="10%" stroke="f" fillcolor="${section.buttonBgColor}">
                      <w:anchorlock/>
                      <center style="color:${section.buttonTextColor};font-family:Arial,sans-serif;font-size:18px;font-weight:bold;">${section.buttonText}</center>
                    </v:roundrect>
                    <![endif]-->
                    <!--[if !mso]><!-->
                    <a href="${buttonUrl}" target="_blank" style="display: inline-block; mso-hide: all; background-color: ${section.buttonBgColor}; color: ${section.buttonTextColor}; padding: 18px 50px; text-decoration: none; font-weight: bold; font-size: 18px; font-family: Arial, Helvetica, sans-serif; border-radius: ${section.buttonBorderRadius}px; text-align: center; min-width: 200px;">
                      ${section.buttonText}
                    </a>
                    <!--<![endif]-->
                  </td>
                </tr>
              </table>`;
    }
  });

  // Recent articles section
  if (includeRecentArticles && recentArticulos && recentArticulos.length > 0) {
    const baseUrl = (process.env.REACT_APP_BACKEND_URL || "") || window.location.origin;
    
    html += `
              <div style="margin-top: 30px; padding: 20px; background-color: #f8fafc; border-radius: 8px;">
                <h3 style="margin: 0 0 15px 0; color: #334155; font-size: 18px; text-align: center; font-family: Arial, sans-serif;">También te puede interesar</h3>
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>`;
    
    recentArticulos.forEach((articulo, index) => {
      // Create a link to view the full article (public view endpoint)
      const articleLink = `${baseUrl}/api/articulos/public/view/${articulo.id}`;
      
      html += `
                    <td style="width: 33.33%; padding: 8px; vertical-align: top;">
                      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                        <tr>
                          <td>
                            <a href="${articleLink}" target="_blank" style="text-decoration: none; display: block;">
                              ${articulo.imagen_url 
                                ? `<img src="${articulo.imagen_url}" alt="${articulo.titulo}" style="width: 100%; height: 120px; object-fit: cover; display: block;" />` 
                                : `<div style="width: 100%; height: 120px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; color: white; font-size: 32px; text-align: center;">📰</div>`}
                            </a>
                          </td>
                        </tr>
                        <tr>
                          <td style="padding: 12px;">
                            <a href="${articleLink}" target="_blank" style="text-decoration: none;">
                              <h4 style="margin: 0 0 8px 0; font-size: 14px; color: #1f2937; line-height: 1.4; font-family: Arial, sans-serif; font-weight: 600;">${articulo.titulo}</h4>
                            </a>
                            <p style="margin: 0 0 10px 0; font-size: 11px; color: #6b7280; font-family: Arial, sans-serif;">${articulo.created_at?.slice(0, 10) || ""}</p>
                            <a href="${articleLink}" target="_blank" style="display: inline-block; padding: 6px 12px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 4px; font-size: 11px; font-family: Arial, sans-serif;">Leer más</a>
                          </td>
                        </tr>
                      </table>
                    </td>`;
    });
    
    html += `
                  </tr>
                </table>
              </div>`;
  }

  html += `
            </td>
          </tr>`; // Close main content

  // Footer — SIEMPRE cubre todo el ancho del marco (igual que el header).
  // Los márgenes globales NO aplican aquí.
  if (footerEnabled) {
    html += `
          <tr>
            <td style="background-color: ${footerSettings.backgroundColor}; padding: ${footerSettings.padding || 10}px 0 ${footerSettings.padding || 10}px 0; text-align: center;">
              ${footerSettings.logoUrl ? `<img src="${footerSettings.logoUrl}" alt="Footer" style="width: 100%; max-width: ${W}px; display: block; margin: 0 0 10px 0; padding: 0;" />` : ""}
              <p style="margin: 0 0 10px 0; color: ${footerSettings.textColor}; font-size: 12px; font-family: Arial, sans-serif;">${footerSettings.text}</p>
              ${footerSettings.showUnsubscribe ? `<a href="#" style="color: ${footerSettings.textColor}; font-size: 11px; font-family: Arial, sans-serif;">Cancelar suscripción</a>` : ""}
            </td>
          </tr>`;
  }

  html += `
        </table>
      </td>
    </tr>
  </table>`;
  return html;
}

// Main Component
export default function VisualEmailEditor({ onSave, onCancel, token, initialHtml = "", initialConfig = null, articulos = [] }) {
  const [sections, setSections] = useState([]);
  const [globalStyles, setGlobalStyles] = useState({
    backgroundColor: "#ffffff",
    padding: 20,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    // Ancho del email en píxeles — controla TODO el marco (header, body, footer).
    // Al cambiarlo, todos los elementos se reajustan automáticamente.
    emailWidth: 600
  });
  const [removingBgFor, setRemovingBgFor] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  // ``rawHtml`` keeps the unresolved {{var}} placeholders — this is what gets
  // saved as the article's contenido_html so the backend can substitute per
  // recipient at send time. ``previewHtml`` is the same HTML with sample data
  // applied, used only inside this editor's preview pane.
  const rawHtmlRef = useRef("");
  
  // Header/Footer settings
  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerSettings, setHeaderSettings] = useState({
    logoUrl: "",
    title: "",
    backgroundColor: "#3b82f6",
    textColor: "#ffffff",
    padding: 10
  });
  const [footerEnabled, setFooterEnabled] = useState(false);
  const [footerSettings, setFooterSettings] = useState({
    text: "© 2026 QAsesores Corredores de Seguros SRL. Todos los derechos reservados.",
    showUnsubscribe: true,
    backgroundColor: "#022239",
    textColor: "#e2e8f0",
    logoUrl: DEFAULT_FOOTER_LOGO_URL,
    padding: 10
  });
  
  // Include recent articles
  const [includeRecentArticles, setIncludeRecentArticles] = useState(false);
  const recentArticulos = articulos.slice(0, 3);
  const [uploadingHeaderLogo, setUploadingHeaderLogo] = useState(false);
  const [uploadingFooterLogo, setUploadingFooterLogo] = useState(false);

  // Send as image — persisted in editor_config so the article remembers the
  // preference. When the user opens "Enviar artículo" later, the dialog will
  // prefill these (handled by parent ArticulosPage when loading the article).
  // Default size: 5.5 × 11 inches (US Letter portrait, like a printed flyer).
  const [enviarComoImagen, setEnviarComoImagen] = useState(false);
  const [imagenAnchoPulgadas, setImagenAnchoPulgadas] = useState(5.5);
  const [imagenAltoPulgadas, setImagenAltoPulgadas] = useState(11);
  const [imagenAutoajuste, setImagenAutoajuste] = useState(true);
  const [imagenLinkUrl, setImagenLinkUrl] = useState("");

  // Format painter (copy text formatting between sections)
  const [copiedTextFormat, setCopiedTextFormat] = useState(null);
  const handleCopyFormat = useCallback((fmt) => {
    setCopiedTextFormat(fmt);
    toast.success("Formato copiado. Pulsa el botón Pegar formato en otra sección.");
  }, []);
  const handlePasteFormat = useCallback(() => {
    // No-op at parent: the actual application happens inline in TextSectionEditor
    // via onChange. This callback is kept for symmetry / future expansion.
  }, []);

  // Floating preview panel
  const [previewFloating, setPreviewFloating] = useState(false);
  const [previewPos, setPreviewPos] = useState({ x: 80, y: 80 });
  const [previewSize, setPreviewSize] = useState({ w: 520, h: 600 });
  const [previewMinimized, setPreviewMinimized] = useState(false);
  // Ref on the right preview panel container — used to implement synchronized
  // scroll between the left editor scroll-area and the preview. When the user
  // scrolls the editor column, the preview column scrolls proportionally so
  // the section being modified stays in view without extra interaction.
  const previewScrollRef = useRef(null);
  const editorScrollRef = useRef(null);
  const syncScrollingRef = useRef(false); // prevent feedback loop
  const handleEditorScroll = useCallback(() => {
    const editor = editorScrollRef.current;
    const preview = previewScrollRef.current;
    if (!editor || !preview) return;
    if (syncScrollingRef.current) return;
    const editorMax = editor.scrollHeight - editor.clientHeight;
    if (editorMax <= 0) return;
    const ratio = editor.scrollTop / editorMax;
    const previewMax = preview.scrollHeight - preview.clientHeight;
    if (previewMax <= 0) return;
    syncScrollingRef.current = true;
    preview.scrollTop = ratio * previewMax;
    requestAnimationFrame(() => { syncScrollingRef.current = false; });
  }, []);

  // Radix ScrollArea wraps its scrollable content in [data-radix-scroll-area-viewport].
  // After mount, replace the refs with those inner viewports and attach the listener.
  useEffect(() => {
    const editorRoot = editorScrollRef.current;
    const previewRoot = previewScrollRef.current;
    if (!editorRoot || !previewRoot) return;
    const editorViewport = editorRoot.querySelector ? editorRoot.querySelector("[data-radix-scroll-area-viewport]") : null;
    const previewViewport = previewRoot.querySelector ? previewRoot.querySelector("[data-radix-scroll-area-viewport]") : null;
    if (!editorViewport || !previewViewport) return;
    // Swap refs so handler reads scroll from viewports directly
    editorScrollRef.current = editorViewport;
    previewScrollRef.current = previewViewport;
    editorViewport.addEventListener("scroll", handleEditorScroll, { passive: true });
    return () => {
      try { editorViewport.removeEventListener("scroll", handleEditorScroll); } catch (_) {}
    };
  }, [handleEditorScroll, previewFloating, previewMinimized]);
  const startFloatDrag = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { ...previewPos };
    const onMove = (ev) => {
      setPreviewPos({
        x: Math.max(0, Math.min(window.innerWidth - 200, startPos.x + (ev.clientX - startX))),
        y: Math.max(0, Math.min(window.innerHeight - 80, startPos.y + (ev.clientY - startY))),
      });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [previewPos]);
  const startFloatResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startSize = { ...previewSize };
    const onMove = (ev) => {
      setPreviewSize({
        w: Math.max(320, Math.min(window.innerWidth - 40, startSize.w + (ev.clientX - startX))),
        h: Math.max(240, Math.min(window.innerHeight - 40, startSize.h + (ev.clientY - startY))),
      });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [previewSize]);

  // Upload logo handler
  const handleUploadLogo = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const setUploading = target === "header" ? setUploadingHeaderLogo : setUploadingFooterLogo;
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API_URL}/api/articulos/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        if (target === "header") {
          setHeaderSettings({ ...headerSettings, logoUrl: data.url });
        } else {
          setFooterSettings({ ...footerSettings, logoUrl: data.url });
        }
        toast.success("Logo cargado correctamente");
      } else {
        toast.error("Error al cargar el logo");
      }
    } catch (error) {
      toast.error("Error al cargar el logo");
    } finally {
      setUploading(false);
    }
  };

  // Load initial configuration if provided
  useEffect(() => {
    if (initialConfig) {
      console.log("Loading initial editor config:", initialConfig);
      if (initialConfig.sections) setSections(initialConfig.sections);
      if (initialConfig.globalStyles) setGlobalStyles(initialConfig.globalStyles);
      if (initialConfig.headerEnabled !== undefined) setHeaderEnabled(initialConfig.headerEnabled);
      if (initialConfig.headerSettings) setHeaderSettings(initialConfig.headerSettings);
      if (initialConfig.footerEnabled !== undefined) setFooterEnabled(initialConfig.footerEnabled);
      if (initialConfig.footerSettings) setFooterSettings(initialConfig.footerSettings);
      if (initialConfig.enviarComoImagen !== undefined) setEnviarComoImagen(!!initialConfig.enviarComoImagen);
      if (initialConfig.imagenAnchoPulgadas !== undefined) setImagenAnchoPulgadas(parseFloat(initialConfig.imagenAnchoPulgadas) || 5.5);
      if (initialConfig.imagenAltoPulgadas !== undefined) setImagenAltoPulgadas(parseFloat(initialConfig.imagenAltoPulgadas) || 11);
      if (initialConfig.imagenAutoajuste !== undefined) setImagenAutoajuste(!!initialConfig.imagenAutoajuste);
      if (initialConfig.imagenLinkUrl !== undefined) setImagenLinkUrl(initialConfig.imagenLinkUrl || "");
      // Backward compat: legacy "imagenAncho" (px) → convert to inches
      if (initialConfig.imagenAncho && initialConfig.imagenAnchoPulgadas === undefined) {
        setImagenAnchoPulgadas(Math.round((initialConfig.imagenAncho / 96) * 10) / 10);
      }
      if (initialConfig.includeRecentArticles !== undefined) setIncludeRecentArticles(initialConfig.includeRecentArticles);
    }
  }, [initialConfig]);

  // Sincronización automática: cuando "Enviar como foto" está activo, el
  // ancho del marco SIEMPRE coincide con el ancho de la foto en píxeles
  // (pulgadas × 96 DPI). Así el HTML que se renderiza a imagen ocupa todo
  // el ancho sin dejar borde lateral.
  useEffect(() => {
    if (enviarComoImagen) {
      const targetW = Math.round((parseFloat(imagenAnchoPulgadas) || 5.5) * 96);
      setGlobalStyles((prev) => (prev.emailWidth === targetW ? prev : { ...prev, emailWidth: targetW }));
    }
  }, [enviarComoImagen, imagenAnchoPulgadas]);

  // Update preview when sections or global styles change. Variables like
  // {{nombre}}, {{empresa}}, etc. are substituted with sample data so the
  // user can preview the final rendered email. The raw HTML (with placeholders)
  // is preserved in rawHtmlRef so the backend can substitute per recipient.
  useEffect(() => {
    // photoMode hace que el HTML se renderice al ancho/alto exacto de la
    // imagen final cuando "Enviar como foto" está activo, así no quedan
    // bordes en blanco al convertir a PNG en el servidor.
    const photoMode = enviarComoImagen
      ? {
          enabled: true,
          widthPx: Math.round((imagenAnchoPulgadas || 5.5) * 96),
          heightPx: Math.round((imagenAltoPulgadas || 11) * 96),
        }
      : null;
    const html = generateHtml(sections, globalStyles, headerEnabled, headerSettings, footerEnabled, footerSettings, includeRecentArticles, recentArticulos, photoMode);
    rawHtmlRef.current = html;
    const sampleContact = {
      nombre: "Juan Pérez",
      email: "juan.perez@empresa.com",
      empresa: "Empresa Ejemplo S.R.L.",
      telefono: "809-555-1234",
      clase_cliente: "VIP",
    };
    const previewWithVars = html.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (match, name) => {
      const v = sampleContact[name.toLowerCase()];
      return v !== undefined ? v : match;
    });
    setPreviewHtml(previewWithVars);
  }, [sections, globalStyles, headerEnabled, headerSettings, footerEnabled, footerSettings, includeRecentArticles, recentArticulos, enviarComoImagen, imagenAnchoPulgadas, imagenAltoPulgadas]);

  const addSection = (type) => {
    const newSection = createSection(type);
    // "Espacio" tiene un propósito explícito: separar secciones. Por eso le
    // damos un alto inicial visible (24px). El resto de secciones nacen sin
    // padding para quedar pegadas — el usuario agrega espacios cuando quiera.
    if (type === "spacer") newSection.padding = 24;
    setSections([...sections, newSection]);
  };

  const insertSectionAt = (type, atIndex) => {
    const newSections = [...sections];
    const newSection = createSection(type);
    if (type === "spacer") newSection.padding = 24;
    newSections.splice(atIndex, 0, newSection);
    setSections(newSections);
  };

  const updateSection = (index, updated) => {
    const newSections = [...sections];
    newSections[index] = updated;
    setSections(newSections);
  };

  const deleteSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sections.length) return;
    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSections(newSections);
  };

  const handleRemoveBackground = async (section) => {
    if (!section.imageUrl) return;
    
    setRemovingBgFor(section.id);
    
    try {
      // Fetch the image and convert to file
      const response = await fetch(section.imageUrl);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append("file", blob, "image.png");

      const res = await fetch(`${API_URL}/api/articulos/remove-background`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        // Update the section with new image URL
        const index = sections.findIndex(s => s.id === section.id);
        if (index !== -1) {
          updateSection(index, { ...section, imageUrl: data.url });
        }
        toast.success("Fondo removido exitosamente");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al remover fondo");
      }
    } catch (error) {
      console.error("Error removing background:", error);
      toast.error("Error al remover fondo");
    } finally {
      setRemovingBgFor(null);
    }
  };

  const handleSave = () => {
    if (sections.length === 0) {
      toast.error("Agrega al menos una sección");
      return;
    }
    
    // Return both HTML and editor configuration
    const editorConfig = {
      sections,
      globalStyles,
      headerEnabled,
      headerSettings,
      footerEnabled,
      footerSettings,
      includeRecentArticles,
      enviarComoImagen,
      imagenAnchoPulgadas,
      imagenAltoPulgadas,
      imagenAutoajuste,
      imagenLinkUrl,
      // Backward compat (px) — derived from inches @ 96 dpi
      imagenAncho: Math.round(imagenAnchoPulgadas * 96)
    };
    
    onSave(rawHtmlRef.current || previewHtml, editorConfig);
  };

  return (
    <div className="h-full flex flex-col" data-testid="visual-email-editor">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b">
        <h3 className="font-semibold">Editor Visual de Email</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="editor-cancel-btn">Cancelar</Button>
          <Button size="sm" onClick={handleSave} data-testid="editor-save-btn">Usar Este Diseño</Button>
        </div>
      </div>

      <div className={`flex-1 ${previewFloating ? 'flex flex-col' : 'grid grid-cols-2 gap-4'} pt-4 min-h-0`}>
        {/* Left: Editor */}
        <div className={`flex flex-col min-h-0 ${previewFloating ? 'flex-1' : ''}`}>
          {/* Global Styles */}
          <div className="p-3 bg-slate-50 rounded-lg mb-4">
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Palette className="w-4 h-4" /> Estilos Globales
            </h4>
            <div className="space-y-3">
              <div className="flex gap-2 items-center">
                <Label className="text-xs w-20 shrink-0">Fondo</Label>
                <input type="color" value={globalStyles.backgroundColor} onChange={(e) => setGlobalStyles({ ...globalStyles, backgroundColor: e.target.value })} className="w-8 h-8 rounded cursor-pointer border" />
                <Input value={globalStyles.backgroundColor} onChange={(e) => setGlobalStyles({ ...globalStyles, backgroundColor: e.target.value })} className="flex-1 text-xs h-8" />
              </div>

              {/* Ancho del marco (email width) — todo el diseño se reajusta a este tamaño */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span className="text-slate-500">↔</span>
                    <span>Ancho del marco</span>
                  </Label>
                  <span className="text-xs font-mono text-blue-600 dark:text-blue-400 tabular-nums">{globalStyles.emailWidth || 600} px</span>
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="range"
                    min={320}
                    max={1600}
                    step={10}
                    value={globalStyles.emailWidth || 600}
                    onChange={(e) => setGlobalStyles({ ...globalStyles, emailWidth: parseInt(e.target.value, 10) || 600 })}
                    disabled={enviarComoImagen}
                    className={`flex-1 accent-blue-600 ${enviarComoImagen ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="email-width-slider"
                  />
                  <Input
                    type="number"
                    min={320}
                    max={1600}
                    value={globalStyles.emailWidth || 600}
                    onChange={(e) => setGlobalStyles({ ...globalStyles, emailWidth: Math.max(320, Math.min(1600, parseInt(e.target.value, 10) || 600)) })}
                    disabled={enviarComoImagen}
                    className={`w-20 h-7 text-xs ${enviarComoImagen ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="email-width-input"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {enviarComoImagen
                    ? `Bloqueado en modo foto — usa el ancho de la imagen (${Math.round((parseFloat(imagenAnchoPulgadas) || 5.5) * 96)} px).`
                    : "Todo el diseño (header, body, footer) se ajusta a este ancho."}
                </p>
              </div>

              <div>
                <Label className="text-xs mb-1 block">Márgenes (px)</Label>
                <div className="grid grid-cols-4 gap-1.5">
                  <div>
                    <span className="text-[10px] text-slate-400">Arriba</span>
                    <Input type="number" min={0} max={80} value={globalStyles.marginTop} onChange={(e) => setGlobalStyles({ ...globalStyles, marginTop: parseInt(e.target.value) || 0 })} className="h-7 text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400">Abajo</span>
                    <Input type="number" min={0} max={80} value={globalStyles.marginBottom} onChange={(e) => setGlobalStyles({ ...globalStyles, marginBottom: parseInt(e.target.value) || 0 })} className="h-7 text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400">Izq</span>
                    <Input type="number" min={0} max={80} value={globalStyles.marginLeft} onChange={(e) => setGlobalStyles({ ...globalStyles, marginLeft: parseInt(e.target.value) || 0 })} className="h-7 text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400">Der</span>
                    <Input type="number" min={0} max={80} value={globalStyles.marginRight} onChange={(e) => setGlobalStyles({ ...globalStyles, marginRight: parseInt(e.target.value) || 0 })} className="h-7 text-xs" />
                  </div>
                </div>
              </div>

              {/* Enviar como foto — toggle + size in inches + clickable URL */}
              <div className="border-t pt-3 mt-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5 cursor-pointer">
                    <ImageIcon className="w-3.5 h-3.5 text-purple-600" />
                    <span className="font-medium">Enviar como foto</span>
                  </Label>
                  <Switch
                    checked={enviarComoImagen}
                    onCheckedChange={setEnviarComoImagen}
                    data-testid="enviar-como-foto-switch"
                  />
                </div>
                {enviarComoImagen && (
                  <div className="mt-2 pl-1 space-y-2 border-l-2 border-purple-200 pl-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">Ancho (pulgadas)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min={1}
                          max={20}
                          value={imagenAnchoPulgadas}
                          onChange={(e) => setImagenAnchoPulgadas(parseFloat(e.target.value) || 5.5)}
                          className="h-7 text-xs"
                          data-testid="imagen-ancho-pulgadas-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Alto (pulgadas)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min={1}
                          max={30}
                          value={imagenAltoPulgadas}
                          onChange={(e) => setImagenAltoPulgadas(parseFloat(e.target.value) || 11)}
                          className="h-7 text-xs"
                          data-testid="imagen-alto-pulgadas-input"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={imagenAutoajuste}
                        onCheckedChange={(c) => setImagenAutoajuste(!!c)}
                        id="imagen-autoajuste"
                        data-testid="imagen-autoajuste-checkbox"
                      />
                      <Label htmlFor="imagen-autoajuste" className="text-[11px] cursor-pointer">
                        Autoajustar contenido al tamaño
                      </Label>
                    </div>
                    <div>
                      <Label className="text-[10px]">URL al hacer clic en la imagen (opcional)</Label>
                      <Input
                        value={imagenLinkUrl}
                        onChange={(e) => setImagenLinkUrl(e.target.value)}
                        placeholder="https://..."
                        className="h-7 text-xs"
                        data-testid="imagen-link-url-input"
                      />
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        Si se completa, el destinatario podrá hacer clic sobre la imagen para abrir esa URL.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Header/Footer Settings */}
          <div className="p-3 bg-slate-100 rounded-lg mb-4 space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" /> Encabezado y Pie
            </h4>
            
            {/* Header Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-xs">Encabezado</Label>
              <Switch checked={headerEnabled} onCheckedChange={setHeaderEnabled} />
            </div>
            {headerEnabled && (
              <div className="pl-4 space-y-2 border-l-2 border-blue-300">
                <div>
                  <Label className="text-xs">Logo del Encabezado</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      value={headerSettings.logoUrl}
                      onChange={(e) => setHeaderSettings({ ...headerSettings, logoUrl: e.target.value })}
                      placeholder="URL o sube una imagen"
                      className="flex-1 h-7 text-xs"
                    />
                    <Button variant="outline" size="sm" asChild disabled={uploadingHeaderLogo} className="h-7 px-2">
                      <label className="cursor-pointer flex items-center gap-1">
                        {uploadingHeaderLogo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        <span className="text-xs">Subir</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadLogo(e, "header")} />
                      </label>
                    </Button>
                  </div>
                  {headerSettings.logoUrl && (
                    <img src={headerSettings.logoUrl} alt="Logo preview" className="h-8 mt-1 rounded" />
                  )}
                </div>
                <div>
                  <Label className="text-xs">Título</Label>
                  <Input
                    value={headerSettings.title}
                    onChange={(e) => setHeaderSettings({ ...headerSettings, title: e.target.value })}
                    placeholder="Nombre de su empresa"
                    className="h-7 text-xs"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Fondo</Label>
                    <div className="flex gap-1">
                      <input type="color" value={headerSettings.backgroundColor} onChange={(e) => setHeaderSettings({ ...headerSettings, backgroundColor: e.target.value })} className="w-6 h-6 rounded cursor-pointer" />
                      <Input value={headerSettings.backgroundColor} onChange={(e) => setHeaderSettings({ ...headerSettings, backgroundColor: e.target.value })} className="flex-1 h-6 text-xs" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Texto</Label>
                    <div className="flex gap-1">
                      <input type="color" value={headerSettings.textColor} onChange={(e) => setHeaderSettings({ ...headerSettings, textColor: e.target.value })} className="w-6 h-6 rounded cursor-pointer" />
                      <Input value={headerSettings.textColor} onChange={(e) => setHeaderSettings({ ...headerSettings, textColor: e.target.value })} className="flex-1 h-6 text-xs" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Padding (px)</Label>
                  <Input type="number" min={0} max={60} value={headerSettings.padding || 10} onChange={(e) => setHeaderSettings({ ...headerSettings, padding: parseInt(e.target.value) || 0 })} className="h-7 text-xs w-20" />
                </div>
              </div>
            )}

            {/* Footer Toggle */}
            <div className="flex items-center justify-between pt-2 border-t">
              <Label className="text-xs">Pie de Página</Label>
              <Switch
                checked={footerEnabled}
                onCheckedChange={(v) => {
                  setFooterEnabled(v);
                  // Al activar, si no hay logoUrl aún, cargar el default QAsesores
                  if (v && !footerSettings.logoUrl) {
                    setFooterSettings({ ...footerSettings, logoUrl: DEFAULT_FOOTER_LOGO_URL });
                  }
                }}
                data-testid="footer-enabled-switch"
              />
            </div>
            {footerEnabled && (
              <div className="pl-4 space-y-2 border-l-2 border-green-300">
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Logo del Pie (opcional)</Label>
                    {footerSettings.logoUrl !== DEFAULT_FOOTER_LOGO_URL && (
                      <button
                        type="button"
                        onClick={() => setFooterSettings({ ...footerSettings, logoUrl: DEFAULT_FOOTER_LOGO_URL })}
                        className="text-[10px] text-blue-600 hover:underline"
                        data-testid="restore-default-footer"
                      >
                        Restaurar default QAsesores
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    <Input
                      value={footerSettings.logoUrl || ""}
                      onChange={(e) => setFooterSettings({ ...footerSettings, logoUrl: e.target.value })}
                      placeholder="URL o sube una imagen"
                      className="flex-1 h-7 text-xs"
                    />
                    <Button variant="outline" size="sm" asChild disabled={uploadingFooterLogo} className="h-7 px-2">
                      <label className="cursor-pointer flex items-center gap-1">
                        {uploadingFooterLogo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        <span className="text-xs">Subir</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadLogo(e, "footer")} />
                      </label>
                    </Button>
                  </div>
                  {footerSettings.logoUrl && (
                    <img src={footerSettings.logoUrl} alt="Footer logo preview" className="h-10 mt-1 rounded border" />
                  )}
                  {footerSettings.logoUrl === DEFAULT_FOOTER_LOGO_URL && (
                    <p className="text-[10px] text-green-700 dark:text-green-400 mt-1 italic">
                      ✓ Usando footer corporativo QAsesores por defecto
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Texto del pie</Label>
                  <Input
                    value={footerSettings.text}
                    onChange={(e) => setFooterSettings({ ...footerSettings, text: e.target.value })}
                    placeholder="© 2026 QAsesores Corredores de Seguros SRL..."
                    className="h-7 text-xs"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={footerSettings.showUnsubscribe}
                    onCheckedChange={(checked) => setFooterSettings({ ...footerSettings, showUnsubscribe: !!checked })}
                  />
                  <Label className="text-xs">Mostrar link de desuscripción</Label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Fondo</Label>
                    <div className="flex gap-1">
                      <input type="color" value={footerSettings.backgroundColor} onChange={(e) => setFooterSettings({ ...footerSettings, backgroundColor: e.target.value })} className="w-6 h-6 rounded cursor-pointer" />
                      <Input value={footerSettings.backgroundColor} onChange={(e) => setFooterSettings({ ...footerSettings, backgroundColor: e.target.value })} className="flex-1 h-6 text-xs" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Texto</Label>
                    <div className="flex gap-1">
                      <input type="color" value={footerSettings.textColor} onChange={(e) => setFooterSettings({ ...footerSettings, textColor: e.target.value })} className="w-6 h-6 rounded cursor-pointer" />
                      <Input value={footerSettings.textColor} onChange={(e) => setFooterSettings({ ...footerSettings, textColor: e.target.value })} className="flex-1 h-6 text-xs" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Padding (px)</Label>
                  <Input type="number" min={0} max={60} value={footerSettings.padding || 10} onChange={(e) => setFooterSettings({ ...footerSettings, padding: parseInt(e.target.value) || 0 })} className="h-7 text-xs w-20" />
                </div>
              </div>
            )}

            {/* Recent Articles Toggle */}
            {recentArticulos.length > 0 && (
              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center gap-2">
                  <Newspaper className="w-4 h-4 text-purple-500" />
                  <Label className="text-xs">Incluir 3 artículos anteriores</Label>
                </div>
                <Switch checked={includeRecentArticles} onCheckedChange={setIncludeRecentArticles} />
              </div>
            )}
          </div>

          {/* Add Section Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(SECTION_TYPES).map(([type, { label, icon: Icon }]) => (
              <Button
                key={type}
                variant="outline"
                size="sm"
                onClick={() => addSection(type)}
                className="gap-1 text-xs"
                data-testid={`add-section-${type}`}
              >
                <Plus className="w-3 h-3" />
                <Icon className="w-3 h-3" />
                {label}
              </Button>
            ))}
          </div>

          {/* Sections List */}
          <ScrollArea className="flex-1" ref={editorScrollRef}>
            {sections.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Agrega secciones para comenzar</p>
                <p className="text-sm">Usa los botones de arriba</p>
              </div>
            ) : (
              <>
                {sections.map((section, index) => (
                  <div key={section.id}>
                    {/* Insert section button - always visible */}
                    <div className="flex items-center justify-center py-1">
                      <div className="flex-1 h-px bg-slate-200" />
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="mx-2 w-6 h-6 rounded-full bg-blue-100 hover:bg-blue-500 hover:text-white text-blue-500 flex items-center justify-center text-xs font-bold transition-colors border border-blue-300 hover:border-blue-500" title="Insertar sección aquí">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-2" side="right">
                          <p className="text-xs font-medium text-slate-500 mb-2">Insertar aquí:</p>
                          <div className="grid grid-cols-1 gap-1">
                            {Object.entries(SECTION_TYPES).map(([type, { icon: SIcon, label }]) => (
                              <button key={type} onClick={() => insertSectionAt(type, index)} className="flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-blue-50 text-slate-700 hover:text-blue-700 w-full text-left">
                                <SIcon className="w-3.5 h-3.5" />
                                {label}
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>
                    <SectionEditor
                      section={section}
                      index={index}
                      total={sections.length}
                      onChange={(updated) => updateSection(index, updated)}
                      onDelete={deleteSection}
                      onMove={moveSection}
                      onRemoveBackground={handleRemoveBackground}
                      isRemovingBg={removingBgFor === section.id}
                      token={token}
                      copiedTextFormat={copiedTextFormat}
                      onCopyFormat={handleCopyFormat}
                      onPasteFormat={handlePasteFormat}
                    />
                  </div>
                ))}
                {/* Insert at the end */}
                <div className="flex items-center justify-center py-1">
                  <div className="flex-1 h-px bg-slate-200" />
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="mx-2 w-6 h-6 rounded-full bg-blue-100 hover:bg-blue-500 hover:text-white text-blue-500 flex items-center justify-center text-xs font-bold transition-colors border border-blue-300 hover:border-blue-500" title="Insertar sección al final">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" side="right">
                      <p className="text-xs font-medium text-slate-500 mb-2">Insertar al final:</p>
                      <div className="grid grid-cols-1 gap-1">
                        {Object.entries(SECTION_TYPES).map(([type, { icon: SIcon, label }]) => (
                          <button key={type} onClick={() => insertSectionAt(type, sections.length)} className="flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-blue-50 text-slate-700 hover:text-blue-700 w-full text-left">
                            <SIcon className="w-3.5 h-3.5" />
                            {label}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              </>
            )}
          </ScrollArea>
        </div>

        {/* Right: Preview with interactive rulers (floating or docked) */}
        <div
          className={`border rounded-lg overflow-hidden bg-gray-100 flex flex-col ${previewFloating ? 'fixed shadow-2xl z-50 border-2 border-blue-400' : ''}`}
          style={previewFloating ? {
            left: previewPos.x,
            top: previewPos.y,
            width: previewSize.w,
            height: previewMinimized ? 44 : previewSize.h,
            transition: 'height 0.2s ease',
          } : undefined}
          data-testid="email-preview-panel"
        >
          <div
            className={`bg-gray-200 px-3 py-2 text-sm font-medium flex items-center gap-2 ${previewFloating ? 'cursor-move select-none' : ''}`}
            onMouseDown={previewFloating ? startFloatDrag : undefined}
          >
            {previewFloating && <Move className="w-3.5 h-3.5 text-blue-500" />}
            <Eye className="w-4 h-4" /> Vista Previa del Email
            <div className="ml-auto flex items-center gap-1">
              {!previewFloating && (
                <span className="text-[10px] text-slate-500 mr-2">Arrastra las líneas para ajustar márgenes</span>
              )}
              {previewFloating && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  title={previewMinimized ? "Expandir" : "Minimizar"}
                  onClick={() => setPreviewMinimized(v => !v)}
                  data-testid="preview-minimize-btn"
                >
                  <Minimize2 className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                title={previewFloating ? "Anclar vista previa al lado derecho" : "Liberar vista previa (modo flotante)"}
                onClick={() => {
                  setPreviewFloating(v => !v);
                  setPreviewMinimized(false);
                }}
                data-testid="preview-toggle-floating-btn"
              >
                {previewFloating ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          {!previewMinimized && (
          <ScrollArea className="flex-1" ref={previewScrollRef}>
            <div className="p-4 bg-gray-100">
              {/* Horizontal ruler (top) */}
              <div className="relative mx-8 mb-1" style={{ height: 20 }}>
                <svg width="100%" height="20" className="text-slate-400">
                  {Array.from({ length: 61 }, (_, i) => {
                    const isMajor = i % 10 === 0;
                    const pct = (i / 60) * 100;
                    return (
                      <g key={i}>
                        <line x1={`${pct}%`} y1={isMajor ? 0 : 10} x2={`${pct}%`} y2={20} stroke="currentColor" strokeWidth={isMajor ? 1 : 0.5} opacity={isMajor ? 0.7 : 0.3} />
                        {isMajor && <text x={`${pct}%`} y={9} textAnchor="middle" fontSize="8" fill="currentColor" opacity="0.6">{Math.round(i * 10)}</text>}
                      </g>
                    );
                  })}
                </svg>
                {/* Left margin dragger */}
                <div
                  className="absolute top-0 bottom-0 cursor-col-resize z-10 group"
                  style={{ left: `${(globalStyles.marginLeft / (globalStyles.emailWidth || 600)) * 100}%`, width: 12, transform: 'translateX(-6px)' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const ruler = e.currentTarget.parentElement;
                    const W = globalStyles.emailWidth || 600;
                    const onMove = (ev) => {
                      const rect = ruler.getBoundingClientRect();
                      const pct = Math.max(0, Math.min(0.4, (ev.clientX - rect.left) / rect.width));
                      setGlobalStyles(prev => ({ ...prev, marginLeft: Math.round(pct * W) }));
                    };
                    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}
                >
                  <div className="w-0.5 h-full bg-blue-500 mx-auto group-hover:bg-blue-600 group-hover:w-1 transition-all" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 rotate-45 group-hover:bg-blue-600" />
                </div>
                {/* Right margin dragger */}
                <div
                  className="absolute top-0 bottom-0 cursor-col-resize z-10 group"
                  style={{ left: `${(((globalStyles.emailWidth || 600) - globalStyles.marginRight) / (globalStyles.emailWidth || 600)) * 100}%`, width: 12, transform: 'translateX(-6px)' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const ruler = e.currentTarget.parentElement;
                    const W = globalStyles.emailWidth || 600;
                    const onMove = (ev) => {
                      const rect = ruler.getBoundingClientRect();
                      const pct = Math.max(0.6, Math.min(1, (ev.clientX - rect.left) / rect.width));
                      setGlobalStyles(prev => ({ ...prev, marginRight: Math.round((1 - pct) * W) }));
                    };
                    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}
                >
                  <div className="w-0.5 h-full bg-blue-500 mx-auto group-hover:bg-blue-600 group-hover:w-1 transition-all" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 rotate-45 group-hover:bg-blue-600" />
                </div>
              </div>

              <div className="relative flex mx-0">
                {/* Vertical ruler (left) */}
                <div className="relative shrink-0" style={{ width: 20 }}>
                  <svg width="20" height="100%" className="text-slate-400 absolute inset-0" preserveAspectRatio="none">
                    {Array.from({ length: 11 }, (_, i) => {
                      const pct = (i / 10) * 100;
                      return (
                        <g key={i}>
                          <line x1={0} y1={`${pct}%`} x2={20} y2={`${pct}%`} stroke="currentColor" strokeWidth={0.5} opacity={0.3} />
                        </g>
                      );
                    })}
                  </svg>
                  {/* Top margin dragger */}
                  <div
                    className="absolute left-0 right-0 cursor-row-resize z-10 group"
                    style={{ top: `${Math.min(40, (globalStyles.marginTop / 10) * 2)}%`, height: 12, transform: 'translateY(-6px)' }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const ruler = e.currentTarget.parentElement;
                      const onMove = (ev) => {
                        const rect = ruler.getBoundingClientRect();
                        const pct = Math.max(0, Math.min(0.4, (ev.clientY - rect.top) / rect.height));
                        setGlobalStyles(prev => ({ ...prev, marginTop: Math.round(pct * 200) }));
                      };
                      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }}
                  >
                    <div className="h-0.5 w-full bg-green-500 my-auto group-hover:bg-green-600 group-hover:h-1 transition-all" style={{ marginTop: 5 }} />
                    <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rotate-45 group-hover:bg-green-600" />
                  </div>
                  {/* Bottom margin dragger */}
                  <div
                    className="absolute left-0 right-0 cursor-row-resize z-10 group"
                    style={{ bottom: `${Math.min(40, (globalStyles.marginBottom / 10) * 2)}%`, height: 12, transform: 'translateY(6px)' }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const ruler = e.currentTarget.parentElement;
                      const onMove = (ev) => {
                        const rect = ruler.getBoundingClientRect();
                        const pct = Math.max(0, Math.min(0.4, 1 - (ev.clientY - rect.top) / rect.height));
                        setGlobalStyles(prev => ({ ...prev, marginBottom: Math.round(pct * 200) }));
                      };
                      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }}
                  >
                    <div className="h-0.5 w-full bg-green-500 my-auto group-hover:bg-green-600 group-hover:h-1 transition-all" style={{ marginTop: 5 }} />
                    <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rotate-45 group-hover:bg-green-600" />
                  </div>
                </div>

                {/* Email preview */}
                <div className="flex-1 min-w-0">
                  {sections.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 bg-white rounded shadow-sm">
                      <p>La vista previa aparecerá aquí</p>
                    </div>
                  ) : (
                    <EmailPreviewFrame
                      html={previewHtml}
                      width={globalStyles.emailWidth || 600}
                    />
                  )}
                </div>
              </div>

              {/* Margin values display */}
              <div className="flex justify-center gap-4 mt-2 text-[10px] text-slate-500">
                <span>Arriba: {globalStyles.marginTop}px</span>
                <span>Abajo: {globalStyles.marginBottom}px</span>
                <span>Izq: {globalStyles.marginLeft}px</span>
                <span>Der: {globalStyles.marginRight}px</span>
              </div>
            </div>
          </ScrollArea>
          )}
          {previewFloating && !previewMinimized && (
            <div
              onMouseDown={startFloatResize}
              className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-blue-400 hover:bg-blue-500 rounded-tl-md flex items-end justify-end opacity-70 hover:opacity-100"
              title="Redimensionar"
              data-testid="preview-resize-handle"
            >
              <svg width="10" height="10" viewBox="0 0 10 10"><path d="M0 10 L10 0 M4 10 L10 4 M8 10 L10 8" stroke="white" strokeWidth="1" /></svg>
            </div>
          )}
        </div>
        {/* Placeholder when preview is floating: keeps left editor visible at full width */}
        {previewFloating && (
          <div className="hidden" />
        )}
      </div>
    </div>
  );
}
